<script lang="ts" setup>
import { ref } from 'vue';
import { useAssistantChat } from '@/hooks/assistant-chat';

const {
  openPanel,
  askQuestion,
} = useAssistantChat();

const draft = ref('');
const featuredQuestions = ref(['开放原子开源基金会是什么？', '如何参与开放原子开源项目？']);

const scenarioCards = [
  {
    title: '找项目',
    description: '根据技术方向了解开放原子重点项目',
  },
  {
    title: '看活动',
    description: '快速定位近期大会、赛事和社区活动',
  },
  {
    title: '问服务',
    description: '理解项目孵化、治理和生态运营支持',
  },
];

const submitDraft = () => {
  const content = draft.value.trim();

  if (!content) {
    return;
  }

  askQuestion(content);

  draft.value = '';
};
</script>

<template>
  <section class="page-home-assistant bg-#F9FCFF pb-96 <md:px-20 <md:pb-64 <xl:px-32">
    <div class="mx-auto max-w-1200 flex items-center gap-x-72 <lg:flex-col <lg:items-stretch <lg:gap-48">
      <div class="w-500 flex-shrink-0 <lg:w-full">
        <div class="inline-flex items-center gap-x-10 border-1 border-#C7E1FF rounded-full border-solid bg-white px-18 py-8 text-14 text-#1281FF font-600 shadow-[0_12px_30px_rgba(18,129,255,0.08)]">
          <span class="h-8 w-8 rounded-full bg-#18C37E shadow-[0_0_0_4px_rgba(24,195,126,0.14)]" aria-hidden="true"></span>
          OpenAtom AI 助手
        </div>

        <h2 class="mb-0 mt-24 text-40 text-#292D33 font-700 leading-56 <md:text-30 <md:leading-42">
          有问题，直接问开放原子 AI 助手
        </h2>

        <p class="mb-0 mt-20 text-18 text-#515B6F font-400 leading-32">
          面向官网访问者提供项目咨询、活动查询、服务了解和参与指引。
        </p>

        <div class="grid grid-cols-3 mt-32 gap-x-14 <md:grid-cols-1 <md:gap-y-12">
          <div
            v-for="card in scenarioCards"
            :key="card.title"
            class="[transition:transform_0.3s_ease,box-shadow_0.3s_ease] rounded-12 bg-white px-18 py-20 shadow-[0_16px_42px_rgba(37,50,75,0.06)] motion-reduce:[transition:none] hover:translate-y--4 hover:shadow-[0_20px_48px_rgba(37,50,75,0.1)] motion-reduce:hover:[transform:none]"
          >
            <div class="text-18 text-#25324B font-700 leading-26">
              {{ card.title }}
            </div>
            <p class="mb-0 mt-8 text-13 text-#6B7280 font-400 leading-20">
              {{ card.description }}
            </p>
          </div>
        </div>

        <div class="mt-40 flex-y-center gap-x-18">
          <button
            type="button"
            class="[transition:transform_0.3s_ease,box-shadow_0.3s_ease] h-54 cursor-pointer border-0 rounded-6 bg-#1281FF px-28 text-16 text-white font-600 leading-24 shadow-[0_14px_30px_rgba(18,129,255,0.22)] motion-reduce:[transition:none] hover:translate-y--2 hover:shadow-[0_18px_36px_rgba(18,129,255,0.28)] motion-reduce:hover:[transform:none]"
            @click="openPanel"
          >
            立即体验
          </button>
          <span class="text-14 text-#9497A1 font-400 leading-22">无需跳转，当前页即可对话</span>
        </div>
      </div>

      <div class="relative flex-1 rounded-28 bg-white p-20 shadow-[0_24px_70px_rgba(37,50,75,0.12)] before:pointer-events-none before:absolute before:right-34 before:top-26 before:h-118 before:w-118 before:rounded-full before:bg-[linear-gradient(135deg,rgba(18,129,255,0.18),rgba(18,129,255,0))] before:content-empty <md:rounded-20 <md:p-12">
        <div class="border-1 border-#E4ECF7 rounded-20 border-solid bg-#FFFFFF p-24">
          <div class="flex items-center justify-between border-b-1 border-b-#E7EEF8 border-b-solid pb-18">
            <div>
              <div class="text-18 text-#25324B font-700 leading-26">
                智能问答预览
              </div>
              <div class="mt-4 text-13 text-#9497A1 leading-20">
                围绕官网内容提供快速引导
              </div>
            </div>
            <div class="h-34 flex-y-center gap-x-6 rounded-full bg-#EAF4FF px-12 text-13 text-#1281FF font-600">
              <span class="h-8 w-8 rounded-full bg-#18C37E shadow-[0_0_0_4px_rgba(24,195,126,0.14)]" aria-hidden="true"></span>
              在线
            </div>
          </div>

          <div class="mt-22 space-y-16">
            <div class="max-w-[78%] rounded-16 rounded-bl-6 bg-#EEF6FF px-16 py-13 text-14 text-#25324B leading-24">
              我可以帮你了解开放原子项目、公益活动、基金会服务和参与方式。
            </div>
            <div class="ml-auto max-w-[78%] rounded-16 rounded-br-6 bg-#1281FF px-16 py-13 text-14 text-white leading-24">
              如何参与开放原子项目？
            </div>
            <div class="max-w-[78%] rounded-16 rounded-bl-6 bg-#EEF6FF px-16 py-13 text-14 text-#25324B leading-24">
              可以先选择感兴趣的技术方向，再查看项目主页中的社区、仓库和贡献指南。
            </div>
          </div>

          <div v-if="featuredQuestions.length > 0" class="mt-24 flex flex-wrap gap-10">
            <button
              v-for="(question, index) in featuredQuestions"
              :key="index"
              type="button"
              class="[transition:all_0.2s_ease] h-36 cursor-pointer border-1 border-#C7E1FF rounded-full border-solid bg-white px-14 text-14 text-#1281FF font-500 motion-reduce:[transition:none] hover:border-#1281FF hover:bg-#1281FF hover:text-white"
              @click="askQuestion(question)"
            >
              {{ question }}
            </button>
          </div>

          <form class="mt-22 flex items-center border-1 border-[rgba(18,129,255,0.08)] rounded-10 border-solid bg-#F1F6FC p-6" @submit.prevent="submitDraft">
            <input
              v-model="draft"
              type="text"
              aria-label="输入想咨询的问题"
              class="h-42 min-w-0 flex-1 border-0 bg-transparent px-14 text-14 text-#292D33 outline-none placeholder:text-#9AA3B2"
              placeholder="试着问：最近有哪些开放原子活动？"
            >
            <button type="submit" class="h-42 flex-shrink-0 cursor-pointer border-0 rounded-8 bg-#1281FF px-20 text-14 text-white font-600">
              发送
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>
